import L from 'leaflet';

const markerCache = {};

export const normalizeMarkerStatus = (raw) => {
  const value = String(raw || '').trim().toLowerCase();
  if (!value) return 'unknown';
  if (['online', 'up', 'healthy', 'normal', 'connected'].includes(value)) return 'healthy';
  if (['degraded', 'warning', 'warn', 'unstable', 'intermittent'].includes(value)) return 'degraded';
  if (['offline', 'down', 'critical', 'disconnected', 'unreachable'].includes(value)) return 'down';
  return 'unknown';
};

export const normalizeMarkerAccessType = (accessType) => (
  String(accessType || '').trim().toUpperCase().includes('HTB') ? 'htb' : 'ftth'
);

const LUCIDE_HOUSE = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
    <path d="M9 21v-6h6v6" />
  </svg>
`;

const LUCIDE_WIFI = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 8.8a15 15 0 0 1 20 0" />
    <path d="M5 12.9a10 10 0 0 1 14 0" />
    <path d="M8.5 16.4a5 5 0 0 1 7 0" />
    <path d="M12 20h.01" />
  </svg>
`;

const LUCIDE_SERVER = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="7" rx="2"></rect>
    <rect x="3" y="13" width="18" height="7" rx="2"></rect>
    <path d="M7 8h.01"></path>
    <path d="M7 17h.01"></path>
  </svg>
`;

const LUCIDE_GIT_BRANCH = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 3v12"></path>
    <path d="M18 9a3 3 0 1 0-3-3"></path>
    <path d="M6 15a3 3 0 1 0 3 3"></path>
    <path d="M9 6h6a3 3 0 0 1 3 3"></path>
  </svg>
`;

const ROUTER_ICON_SRC = '/images/router-ktc.png';

const LUCIDE_TRIANGLE_ALERT = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <path d="M12 9v4"></path>
    <path d="M12 17h.01"></path>
  </svg>
`;

const createCachedDivIcon = (cacheKey, payload) => {
  if (markerCache[cacheKey]) return markerCache[cacheKey];
  const icon = L.divIcon(payload);
  markerCache[cacheKey] = icon;
  return icon;
};

export const createCustomerMarkerIcon = ({
  status,
  selected = false,
  anomaly = false,
  predictive = false,
  accessType = 'FTTH_PPPOE',
  size = 36,
} = {}) => {
  const markerStatus = normalizeMarkerStatus(status);
  const access = normalizeMarkerAccessType(accessType);
  const isCritical = markerStatus === 'down' || !!anomaly;
  const cacheKey = `customer:${markerStatus}:${access}:${selected ? 'selected' : 'idle'}:${anomaly ? 'anomaly' : 'normal'}:${predictive ? 'predict' : 'plain'}:${size}`;
  const predictiveBadge = predictive ? '<span class="customer-predict-badge">AI</span>' : '';
  const pulseNode = (isCritical || selected) ? '<span class="customer-marker-pulse"></span>' : '';
  const glyph = access === 'htb' ? LUCIDE_WIFI : LUCIDE_HOUSE;
  const anomalyOverlay = anomaly ? `<span class="marker-anomaly-badge">${LUCIDE_TRIANGLE_ALERT}</span>` : '';
  return createCachedDivIcon(cacheKey, {
    className: 'customer-marker-wrapper',
    html: `<div class="map-marker-base customer-marker customer-${access} state-${markerStatus}${selected ? ' is-selected' : ''}${anomaly ? ' is-anomaly' : ''}${isCritical ? ' is-critical' : ''}">${predictiveBadge}${anomalyOverlay}<span class="customer-marker-ring"></span>${pulseNode}<span class="customer-marker-glyph">${glyph}</span></div>`,
    iconSize: [size, size],
    iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
    popupAnchor: [0, -Math.round(size / 2)],
  });
};

export const createCustomerClusterMarkerIcon = ({
  count = 0,
  status = 'unknown',
  accessType = 'ftth',
  selected = false,
  anomaly = false,
} = {}) => {
  const markerStatus = normalizeMarkerStatus(status);
  const access = normalizeMarkerAccessType(accessType);
  const size = selected ? 42 : 38;
  const cacheKey = `cluster:${count}:${markerStatus}:${access}:${selected ? 'selected' : 'idle'}:${anomaly ? 'anomaly' : 'normal'}`;
  return createCachedDivIcon(cacheKey, {
    className: 'customer-cluster-wrapper',
    html: `
      <div class="customer-cluster-marker customer-${access} state-${markerStatus} ${selected ? 'is-selected' : ''} ${anomaly ? 'is-anomaly' : ''}">
        <span class="customer-cluster-ring"></span>
        <span class="customer-cluster-count">${Math.max(1, Number(count || 0))}</span>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
    popupAnchor: [0, -Math.round(size / 2)],
  });
};

export const createInfrastructureMarkerIcon = ({
  type = 'router',
  status = 'unknown',
  selected = false,
  anomaly = false,
  focused = false,
  rootCause = false,
} = {}) => {
  const markerStatus = normalizeMarkerStatus(status);
  const markerType = String(type || '').trim().toLowerCase();
  if (markerType === 'router' || markerType === 'nas') {
    const size = selected ? 42 : 38;
    const cacheKey = `infra:router:imgv1:${markerStatus}:${selected ? 'selected' : 'idle'}:${anomaly ? 'anomaly' : 'normal'}`;
    const anomalyOverlay = anomaly ? `<span class="marker-anomaly-badge">${LUCIDE_TRIANGLE_ALERT}</span>` : '';
    return createCachedDivIcon(cacheKey, {
      className: 'ktc-router-marker-wrapper',
      html: `
        <div class="map-marker-base ktc-router-marker marker-type-router state-${markerStatus} ${selected ? 'is-selected' : ''} ${anomaly ? 'is-anomaly' : ''}">
          ${anomalyOverlay}
          <span class="ktc-router-glow"></span>
          <span class="ktc-router-core"><img class="ktc-router-image" src="${ROUTER_ICON_SRC}" alt="Router" /></span>
        </div>`,
      iconSize: [size, size],
      iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
      popupAnchor: [0, -16],
    });
  }

  const normalizedType = markerType === 'olt' ? 'olt' : 'odp';
  const markerSizeBase = normalizedType === 'olt' ? 42 : 34;
  const markerSize = rootCause ? markerSizeBase + 8 : ((focused || selected) ? markerSizeBase + 4 : markerSizeBase);
  const cacheKey = `infra:${normalizedType}:${markerStatus}:${rootCause ? 'root' : 'base'}:${(focused || selected) ? 'focus' : 'idle'}`;
  const glyph = normalizedType === 'olt' ? LUCIDE_SERVER : LUCIDE_GIT_BRANCH;
  const anomalyFlag = rootCause || anomaly;
  const anomalyOverlay = anomalyFlag ? `<span class="marker-anomaly-badge">${LUCIDE_TRIANGLE_ALERT}</span>` : '';
  return createCachedDivIcon(cacheKey, {
    className: 'ktc-node-wrapper',
    html: `<div class="map-marker-base ktc-node-marker marker-type-${normalizedType} ktc-node-${normalizedType} ktc-node-${markerStatus}${rootCause ? ' ktc-node-root-cause' : ''}${(focused || selected) ? ' ktc-node-focused' : ''}${anomalyFlag ? ' is-anomaly' : ''}">
      ${anomalyOverlay}
      <span class="ktc-node-glow"></span>
      <span class="ktc-node-core">${glyph}</span>
    </div>`,
    iconSize: [markerSize, markerSize],
    iconAnchor: [Math.round(markerSize / 2), Math.round(markerSize / 2)],
    popupAnchor: [0, -18],
  });
};
